import type { StateCreator } from 'zustand';
import type {
  DefinitionFormStoreState,
  WeightingMethodSlice,
} from '../types/definitionFormStoreTypes';
import type {
  ConcentrationLimitsState,
  TaxLossHarvestingState,
} from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/WeightingMethod/types';
import { DEFAULT_TRANSACTION_COSTS } from '@/features/Strategies/features/SmartStrategyDefinitonForm/Steps/WeightingMethod/types';

const initialConcentrationLimits: ConcentrationLimitsState = {
  enabled: false,
  position: { enabled: false, min: null, max: 7.5 },
  sector: { enabled: false, min: 0, max: 35 },
  country: { enabled: false, min: 0, max: 50 },
  enforceDuringOptimization: true,
};

const initialTaxLossHarvesting: TaxLossHarvestingState = {
  enabled: false,
  maxTrackingError: 3,
  longTermTaxRate: 20,
  shortTermTaxRate: 37,
  saleLotMethod: 'HIFO',
  opportunisticHarvest: false,
  minLossPctToHarvest: 5,
  maxRealizedGainDollars: 0,
  washSaleWindowDays: 30,
  minHoldingDays: 5,
  maxNetRealizedGainDollars: 0,
  taxPenaltyMultiplier: 0.00001,
};

export const createWeightingMethodSlice: StateCreator<
  DefinitionFormStoreState,
  [['zustand/immer', never]],
  [],
  WeightingMethodSlice
> = (set) => ({
  // Default weighting mirrors the starting universe's native scheme. The
  // supported universes are all market-cap-weighted indices (the universe model
  // carries no per-index weighting field), so the wizard starts "Synced to
  // Universe" on Market-Cap Weighted — making the Step-4 badge truthful and
  // honoring the principle that an untouched config reproduces the index. This
  // is the exact state a user reaches by selecting the "simple" category then
  // the "market_cap" model (params = getDefaultParams('market_cap') in
  // WeightingMethod/data.ts).
  gen_config: {
    weighting_strategy: 'market_cap',
    weighting_params: {},
  },
  weightingCategory: 'simple',
  weightingParams: { cap_basis: 'free_float', currency_normalization: 'on' },
  weightingSelectedMetrics: [],
  concentrationLimits: initialConcentrationLimits,
  taxLossHarvesting: initialTaxLossHarvesting,
  transactionCosts: DEFAULT_TRANSACTION_COSTS,
  totalCapital: 1_000_000,

  setWeightingStrategy: (strategy) =>
    set((state) => {
      state.gen_config.weighting_strategy = strategy;
    }),

  setWeightingCategory: (category) =>
    set((state) => {
      state.weightingCategory = category;
    }),

  setWeightingParams: (params) =>
    set((state) => {
      state.weightingParams = params;
    }),

  setWeightingSelectedMetrics: (metrics) =>
    set((state) => {
      state.weightingSelectedMetrics = metrics;
    }),

  setConcentrationLimits: (limits) =>
    set((state) => {
      state.concentrationLimits = limits;
    }),

  setTaxLossHarvesting: (tlh) =>
    set((state) => {
      state.taxLossHarvesting = tlh;
    }),

  setTransactionCosts: (costs) =>
    set((state) => {
      state.transactionCosts = costs;
    }),

  setTotalCapital: (capital) =>
    set((state) => {
      state.totalCapital = capital;
    }),
});
